// 极简的全局缓存基类：按 key 存储固定结果，尊重 TTL

interface CacheEntry<T = any> {
  value?: T;
  expiresAt: number;
  inflight?: Promise<T>;
}

import { useCallback, useEffect, useMemo, useState } from 'react';

export class GlobalCacheStore {
  private store = new Map<string, CacheEntry<any>>();

  /**
   * 获取固定结果：未命中或过期则调用 fetcher 并缓存
   * 重试/超时策略由调用方的 fetcher 自己实现
   */
  async get<T>(
    key: string,
    ttlMs: number,
    fetcher: () => Promise<T> | T
  ): Promise<T> {
    const now = Date.now();
    const cached = this.store.get(key) as CacheEntry<T> | undefined;
    if (cached && cached.value !== undefined && cached.expiresAt > now) {
      return cached.value as T;
    }
    if (cached?.inflight) {
      return cached.inflight;
    }

    const inflight = Promise.resolve().then(() => fetcher());
    this.store.set(key, { ...cached, inflight, expiresAt: now + ttlMs });
    try {
      const value = await inflight;
      this.store.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    } catch (err) {
      const current = this.store.get(key) as CacheEntry<T> | undefined;
      if (current?.inflight === inflight) {
        this.store.set(key, { ...current, inflight: undefined, expiresAt: 0 });
      }
      throw err;
    }
  }

  /** 手动设置值（预热或注入） */
  set<T>(key: string, value: T, ttlMs: number): void {
    const expiresAt = Date.now() + ttlMs;
    this.store.set(key, { value, expiresAt });
  }

  /** 清除指定 key */
  clear(key: string): void {
    this.store.delete(key);
  }
}

// 单例导出，用于全局缓存
export const globalCacheStore = new GlobalCacheStore();

export default globalCacheStore;

type DefaultResult<T> = {
  list: T[];
  map: Map<number, T>;
  loading: boolean;
  error: any;
  reload: (params?: any) => Promise<void>;
};

type AliasedResult<T, S extends string> = {
  [K in `${S}List`]: T[];
} & {
  [K in `${S}Map`]: Map<number, T>;
} & {
  [K in `${S}Loading`]: boolean;
} & {
  [K in `${S}Error`]: any;
} & {
  [K in `${S}Reload`]: (params?: any) => Promise<void>;
};

export function createCachedHook<T, S extends string>(options: {
  cacheKey: string;
  ttlMsDefault: number;
  fetchItems: (params?: any) => Promise<T[]>;
  keySelector?: (item: T) => number;
  subjectKey: S;
}): (ttlMs?: number) => AliasedResult<T, S>;
export function createCachedHook<T>(options: {
  cacheKey: string;
  ttlMsDefault: number;
  fetchItems: (params?: any) => Promise<T[]>;
  keySelector?: (item: T) => number;
  subjectKey?: undefined;
}): (ttlMs?: number) => DefaultResult<T>;
export function createCachedHook<T, S extends string>(options: {
  cacheKey: string;
  ttlMsDefault: number;
  fetchItems: (params?: any) => Promise<T[]>;
  keySelector?: (item: T) => number;
  subjectKey?: S;
}) {
  return function useCached(ttlMs?: number) {
    const [list, setList] = useState<T[]>([]);
    const [map, setMap] = useState<Map<number, T>>(new Map());
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    const reload = useCallback(async (params?: any) => {
      setLoading(true);
      setError(null);
      try {
        const key = options.cacheKey + ':' + JSON.stringify(params || {});
        const items = await globalCacheStore.get<T[]>(key, ttlMs ?? options.ttlMsDefault, async () => {
          const res = await options.fetchItems(params);
          return res;
        });
        setList(items as T[]);
        const m = new Map<number, T>();
        if (options.keySelector) {
          for (const item of items as T[]) {
            const k = options.keySelector(item);
            if (!Number.isNaN(k)) {
              m.set(k, item);
            }
          }
        }
        setMap(m);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    }, [ttlMs]);

    useEffect(() => {
      reload();
    }, [reload]);

    if (options.subjectKey) {
      const base = options.subjectKey as string;
      const result: any = {};
      result[`${base}List`] = list;
      result[`${base}Map`] = map;
      result[`${base}Loading`] = loading;
      result[`${base}Error`] = error;
      result[`${base}Reload`] = reload;
      return result;
    }
    return { list, map, loading, error, reload } as DefaultResult<T>;
  };
}
